/**
 * ScrollIntentArbiter — gates programmatic auto-scroll behind recent user-scroll
 * intent (Bug #1, ADR-0010 D27/D28). The user's own scroll (touch / wheel /
 * scrollbar / keyboard) always wins: a programmatic `scrollActiveIntoView`
 * NEVER fires while the user is actively scrolling, and an in-flight smooth
 * auto-scroll ABORTS the moment a user scroll begins.
 *
 * It is a standalone, JTL-/DOM-agnostic unit (clock injected) so it is unit-
 * testable with a fake clock and **reusable** by both finders' tracks — it lives
 * next to `ColumnTrack` (the shared horizontal-behaviour module, D24) because the
 * two horizontal concerns (scroll-into-view + intent gating) belong together.
 *
 * Wiring: the track/columns feed `notifyUserScroll()` from passive
 * scroll/touch/wheel listeners; `ColumnTrack.scrollIntoView` routes through
 * `requestAutoScroll(fn)`. There is NO `preventDefault` and NO touch-gesture
 * routing here — axis routing stays native (D26); this unit only arbitrates the
 * auto-scroll-vs-user-scroll conflict.
 *
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/spec.md §3.7
 */

export interface ScrollIntentArbiterOptions {
  /** Injected monotonic clock (ms). Defaults to `performance.now`. */
  now?: () => number;
  /**
   * The suppression window (ms): an auto-scroll is suppressed if a user scroll
   * occurred within this many ms. Tuned in validation (Information Gap §5.6).
   */
  windowMs?: number;
}

/** Cancels an in-flight smooth auto-scroll (e.g. a snap to the current offset). */
export type AbortHandle = () => void;

const DEFAULT_WINDOW_MS = 200;

export class ScrollIntentArbiter {
  private readonly now: () => number;
  private readonly windowMs: number;
  /** Timestamp of the last user-scroll signal, or `-Infinity` if none. */
  private lastUserScrollAt = Number.NEGATIVE_INFINITY;
  /** The abort handle of the auto-scroll currently in flight, if any. */
  private inFlightAbort: AbortHandle | null = null;

  constructor(options: ScrollIntentArbiterOptions = {}) {
    this.now = options.now ?? (() => performance.now());
    this.windowMs = options.windowMs ?? DEFAULT_WINDOW_MS;
  }

  /**
   * Record that the user is actively scrolling. Called (passively) from the
   * track/column scroll + touch + wheel listeners. Also aborts any in-flight
   * smooth auto-scroll so the user immediately takes over.
   */
  notifyUserScroll(): void {
    this.lastUserScrollAt = this.now();
    if (this.inFlightAbort) {
      const abort = this.inFlightAbort;
      this.inFlightAbort = null; // clear BEFORE calling so it fires at most once
      abort();
    }
  }

  /**
   * Run `fn` (the programmatic auto-scroll) ONLY if the user has not scrolled
   * within the intent window. While in flight, `fn` should register its cancel
   * via `registerAbort` so a user scroll can abort it.
   */
  requestAutoScroll(fn: () => void): void {
    if (this.now() - this.lastUserScrollAt < this.windowMs) return; // user wins
    fn();
  }

  /**
   * Register the cancel handle for the auto-scroll started inside the current
   * `requestAutoScroll` callback. A subsequent `notifyUserScroll` invokes it.
   */
  registerAbort(abort: AbortHandle): void {
    this.inFlightAbort = abort;
  }

  /** The smooth auto-scroll finished on its own — drop its abort handle. */
  settle(): void {
    this.inFlightAbort = null;
  }
}
